/* eslint-disable no-restricted-syntax */
import React, { useEffect, useMemo, useRef } from 'react';
import { isEmpty, isBoolean, get, set, flatMap, cloneDeep } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useProductCostingState } from '../ProductCostingContext';
import {
  cloneData,
  checkCurrency,
  shortenStringWithTooltip,
  roundOff,
  roundingOff,
  sumPricePerUnitTestingAdditionalSection,
} from '../../../../Components/helper';
import BombiixAGGrid, { HierExpandCell, ExpandChevronCell } from '../../../../Components/BombiixAGGrid';
import type { BombiixCellRendererParams, BombiixColumnDefOrGroup } from '../../../../Components/BombiixAGGrid/types';
import useShowHideSectionHooks from '../Hooks/useShowHideSectionHooks';
import type { GrandTotalState, LandedInfoState } from '../types';
import { useLengthLabel } from '../../../../hooks/useLengthLabel';


/** Anything the table displays by `name`. */
interface NamedRef {
  name?: string;
}

/** One expanded colourway/size/length line under a grand-total row. */
interface GrandTotalExpandRow {
  colourway?: { _id?: string } | undefined;
  size?: string | undefined;
  length?: (NamedRef & { _id?: string }) | undefined;
  pricePerUnit?: unknown;
  quantityPerSizCol?: unknown;
  totalPrice?: unknown;
  multiQuantityRows?: Array<{ quantityPerSizCol?: unknown; pricePerUnit?: unknown; totalPrice?: unknown }> | undefined;
  defaultRowIndex?: number | undefined;
  isExcluded?: boolean | undefined;
  totalPriceBase?: unknown;
  [key: string]: unknown;
}

/** One grand-total parent row — an average-costing or quantities row with the totals written on. */
interface GrandTotalRow {
  colourway?: NamedRef | undefined;
  sizeRange?: NamedRef[] | undefined;
  expandData?: GrandTotalExpandRow[] | undefined;
  isGTExpanded?: boolean | undefined;
  isExpanded?: boolean | undefined;
  sumOfQuantityPerSizCol?: unknown;
  sumOfTotalPrice?: unknown;
  sumTotalPrice?: unknown;
  totWeightagePricePerUnit?: unknown;
  totalQuantity?: unknown;
  [key: string]: unknown;
}

/**
 * One rendered row. Declared with `type` rather than `interface`: BombiixAGGrid constrains its row
 * generic to `Record<string, unknown> & {…}`, and only type aliases receive the implicit index
 * signature.
 */
type GrandTotalGridRow = {
  __cagId: string;
  __rowType: 'summary' | 'data' | 'grandTotal';
  parentIndex?: number | undefined;
  __showChevron?: boolean | undefined;
  isExpanded?: boolean | undefined;
  colourway?: string | undefined;
  size?: React.ReactNode | undefined;
  length?: React.ReactNode | undefined;
  quantity?: unknown;
  price?: React.ReactNode | undefined;
  total?: React.ReactNode | undefined;
};

interface ExpandAllHeaderProps {
  allExpanded: boolean;
  onToggleAll: (next: boolean) => void;
}

/**
 * Expand/collapse-all chevron for this table's header.
 *
 * BombiixAGGrid ships its own ExpandAllHeader, but that one drives AG Grid master/detail nodes.
 * This table fakes its hierarchy with flat rows, so expansion is caller-owned and so is the header.
 */
function ExpandAllHeader(props: ExpandAllHeaderProps): React.JSX.Element {
  const { allExpanded, onToggleAll } = props;
  return (
    <div className="cag-header cag-header--expand-all">
      <ExpandChevronCell
        expanded={Boolean(allExpanded)}
        onToggle={() => onToggleAll(!allExpanded)}
        expandLabel="Expand all"
        collapseLabel="Collapse all"
      />
    </div>
  );
}

type ReadonlyNodeCellProps = BombiixCellRendererParams<GrandTotalGridRow> & {
  accessor?: (row: GrandTotalGridRow | undefined) => React.ReactNode;
};

/** Read-only cell that renders whatever node the accessor returns — the total column returns JSX. */
function ReadonlyNodeCell(params: ReadonlyNodeCellProps): React.JSX.Element {
  const { data, accessor } = params;
  const content = typeof accessor === 'function' ? accessor(data) : (params.value as React.ReactNode);
  const isEmptyContent = content === undefined || content === null || content === '';
  return (
    <div className="cag-cell cag-cell-readonly">
      <span>{isEmptyContent ? '' : content}</span>
    </div>
  );
}

export function GrandTotal(): React.JSX.Element {
  const {
    productCostingstate,
    setGrandTotalState,
    additionalState,
    testingState,
    grandTotalState,
    colSizeLengthToggleState,
    landedInfoState,
    detailedQuanitiesState,
    detailedCostingState,
    isAllExpandedState,
    setExpandAllState,
    additionalComponentState,
  } = useProductCostingState();
  const { t } = useTranslation('common');
  const lengthLabel = useLengthLabel(t('DEVLOPMENT_ORDER.EXPAND_VIEW.GRAND_TOTAL.LENGTH'));
  const { grandTotalAllExpand } = isAllExpandedState;
  const landedInfoRef = useRef<LandedInfoState>(landedInfoState);
  const {
    averageCosting: { averageCostingInfo, averageCostingActive, totLength, totSize },
    productInfo: { currency },
    isBreakdown,
    costingSizes,
  } = productCostingstate;

  const {
    detailedCosting: { bomTypeArr, detailedCostingActive },
  } = detailedCostingState;

  const {
    quantities: { quantitiesInfo },
    quantities,
  } = detailedQuanitiesState;

  const {
    additional: { additionalInfo },
  } = additionalState;

  const {
    additionalComponent: { additionalComponentInfo },
  } = additionalComponentState;
  const {
    testing: { testingInfo },
  } = testingState;
  const { grandTotal, totalOverAllSumTotalPrice } = grandTotalState;
  const { hideLengthCol, hideColourCol, hideSizeCol } = useShowHideSectionHooks();

  const { colourWayToggle, sizeLengthToggle } = colSizeLengthToggleState;
  const colourWayToggleRef = useRef<boolean>(colourWayToggle);
  const getFilteredAverageCosting = (sourceRows: GrandTotalRow[] = [], isDetailedQuantites = false): GrandTotalRow[] => {
    const updatedAverageCosting: GrandTotalRow[] = [];
    const hasValidPriceAndQty = (item: GrandTotalRow | GrandTotalExpandRow): boolean =>
      isDetailedQuantites
        ? parseFloat(String(item?.quantityPerSizCol || 0)) > 0
        : parseFloat(String(item?.pricePerUnit || 0)) > 0 && parseFloat(String(item?.quantityPerSizCol || 0)) > 0;
    for (const element of sourceRows) {
      if (Array.isArray(element.expandData) && element.expandData.length > 0) {
        const filteredExpandData = element?.expandData?.filter(hasValidPriceAndQty);
        if (filteredExpandData.length > 0) {
          updatedAverageCosting.push({
            ...element,
            expandData: filteredExpandData,
          });
        }
      } else if (hasValidPriceAndQty(element)) {
        updatedAverageCosting.push({ ...element });
      }
    }

    return updatedAverageCosting;
  };

  useEffect(() => {
    colourWayToggleRef.current = colourWayToggle;
  }, [colourWayToggle]);

  useEffect(() => {
    landedInfoRef.current = landedInfoState;
  }, [landedInfoState]);

  useEffect(() => {
    if (!averageCostingActive) return;
    const callFn = async (): Promise<void> => {
      if (Array.isArray(averageCostingInfo) && averageCostingInfo?.length > 0) {
        const cloneTestingState = cloneDeep(testingInfo);
        const copyAverageCosting: GrandTotalRow[] = cloneDeep(averageCostingInfo);
        const sumPricePerUnitTestingSectionData = sumPricePerUnitTestingAdditionalSection(cloneTestingState, colourWayToggleRef?.current, 'pricePerUnit');

        const cloneAdditionalState = cloneDeep(additionalInfo)?.filter((item: { isExcluded?: boolean }) => !item.isExcluded);
        const cloneAdditionalComponentState = cloneDeep(additionalComponentInfo)?.filter((item: { isExcluded?: boolean }) => !item.isExcluded);

        const sumPricePerUnitAdditionalSectionData = sumPricePerUnitTestingAdditionalSection(cloneAdditionalState, colourWayToggleRef?.current, 'totalPrice');
        const sumPricePerUnitAdditionalComponentSectionData = sumPricePerUnitTestingAdditionalSection(
          cloneAdditionalComponentState,
          colourWayToggleRef?.current,
          'totalPrice',
        );

        const filteredAverageCosting = Array.isArray(copyAverageCosting) ? getFilteredAverageCosting(copyAverageCosting) : [];

        if (Array.isArray(filteredAverageCosting) && filteredAverageCosting.length > 0) {
          const grandTotalAverageCosting: GrandTotalRow[] = [];
          let totalOverAllSumTotalPrice: string | number = '0';
          for (const averCosting of filteredAverageCosting) {
            let sumOfTotalPrice = 0;
            let sumTotalPrice = 0;
            let totWeightagePricePerUnit = 0;
            let totalQuantity = 0;
            averCosting.isGTExpanded = false;

            const grandTotalExpandData: GrandTotalExpandRow[] = [];

            if (Array.isArray(averCosting.expandData) && averCosting.expandData.length > 0) {
              for (const expandData of averCosting.expandData) {
                const expandPrice = Number(expandData?.pricePerUnit || 0);
                const expandQty = Number(expandData?.quantityPerSizCol || 0);

                let colorWiseTesting = 0;
                let colorWiseAdditional = 0;
                let colorWiseAdditionalComponent = 0;

                if (expandData?.colourway?._id) {
                  colorWiseTesting = colourwayShare(sumPricePerUnitTestingSectionData, expandData.colourway._id);
                  colorWiseAdditional = colourwayShare(sumPricePerUnitAdditionalSectionData, expandData.colourway._id);
                  colorWiseAdditionalComponent = colourwayShare(sumPricePerUnitAdditionalComponentSectionData, expandData.colourway._id);
                } else {
                  colorWiseTesting = Number(sumPricePerUnitTestingSectionData || 0);
                  colorWiseAdditional = Number(sumPricePerUnitAdditionalSectionData || 0);
                  colorWiseAdditionalComponent = Number(sumPricePerUnitAdditionalComponentSectionData || 0);
                }

                const pricePerUnit = colorWiseTesting + colorWiseAdditional + colorWiseAdditionalComponent + expandPrice;
                const totalPrice = pricePerUnit * expandQty;

                expandData.pricePerUnit = pricePerUnit;
                expandData.totalPrice = totalPrice;
                totalOverAllSumTotalPrice = Number(totalOverAllSumTotalPrice) + Number(totalPrice);

                totalQuantity = Number(totalQuantity) + Number(expandQty);
                totWeightagePricePerUnit = Number(totWeightagePricePerUnit) + Number(pricePerUnit);
                sumOfTotalPrice = Number(sumOfTotalPrice) + Number(totalPrice);
                sumTotalPrice = Number(sumTotalPrice) + Number(totalPrice);

                grandTotalExpandData.push(expandData);
              }
              averCosting.expandData = grandTotalExpandData;
            }
            averCosting.sumOfTotalPrice = sumOfTotalPrice;
            averCosting.sumTotalPrice = sumTotalPrice;

            averCosting.totWeightagePricePerUnit = totWeightagePricePerUnit;
            averCosting.totalQuantity = totalQuantity;
            grandTotalAverageCosting.push(averCosting);
          }
          setGrandTotalState((prevState) => ({
            ...prevState,
            grandTotal: grandTotalAverageCosting,
            totalOverAllSumTotalPrice,
          }));
        } else {
          setGrandTotalState((prevState) => ({
            ...prevState,
            grandTotal: [],
            totalOverAllSumTotalPrice: '0',
          }));
        }
      }
    };
    callFn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [averageCostingActive, averageCostingInfo, testingInfo, additionalInfo, additionalComponentInfo]);

  const bomCombinationPricePerUnit = (data?: GrandTotalExpandRow[]): Record<string, number> => {
    const grouped: Record<string, number> = {};

    data?.forEach((item) => {
      if (item?.isExcluded) return;
      const colourwayId = item.colourway?._id ?? 'null';
      const size = item?.size ?? 'null';
      const length = item?.length?._id ?? 'null';
      const key = `${colourwayId}_${size}_${length}`;
      const price = parseFloat(String(item?.totalPrice ?? item?.totalPriceBase ?? '0'));

      if (!grouped[key]) {
        grouped[key] = 0;
      }
      grouped[key] = (grouped[key] ?? 0) + price;
    });
    return grouped;
  };

  useEffect(() => {
    if (!detailedCostingActive) return;
    const callFn = async (): Promise<void> => {
      const cloneQuantities = cloneData(quantitiesInfo);
      if (Array.isArray(cloneQuantities) && cloneQuantities?.length > 0) {
        const cloneTestingState = cloneDeep(testingInfo);
        const copyQuantities: GrandTotalRow[] = cloneDeep(cloneQuantities);
        let cloneBomArr = cloneDeep(bomTypeArr);

        const sumPricePerUnitTestingSectionData = sumPricePerUnitTestingAdditionalSection(cloneTestingState, colourWayToggleRef?.current, 'pricePerUnit');

        const cloneAdditionalState = cloneDeep(additionalInfo)?.filter((item: { isExcluded?: boolean }) => !item.isExcluded);
        const cloneAdditionalComponentState = cloneDeep(additionalComponentInfo)?.filter((item: { isExcluded?: boolean }) => !item.isExcluded);
        const sumPricePerUnitAdditionalSectionData = sumPricePerUnitTestingAdditionalSection(cloneAdditionalState, colourWayToggleRef?.current, 'totalPrice');
        const sumPricePerUnitAdditionalComponentSectionData = sumPricePerUnitTestingAdditionalSection(
          cloneAdditionalComponentState,
          colourWayToggleRef?.current,
          'totalPrice',
        );

        cloneBomArr = flatMap(cloneBomArr?.map((val: { expandData?: GrandTotalExpandRow[] }) => val?.expandData));
        cloneBomArr = bomCombinationPricePerUnit(cloneBomArr);

        const filteredQuantities = Array.isArray(copyQuantities) ? getFilteredAverageCosting(copyQuantities, true) : [];
        if (Array.isArray(filteredQuantities) && filteredQuantities.length > 0) {
          const grandTotalDetailedCosting: GrandTotalRow[] = [];
          let totalOverAllSumTotalPrice: string | number = '0';
          for (const dCosting of filteredQuantities) {
            let sumOfTotalPrice = 0;
            let sumTotalPrice = 0;
            let totWeightagePricePerUnit = 0;
            let totalQuantity = 0;
            dCosting.isGTExpanded = false;

            const grandTotalExpandData: GrandTotalExpandRow[] = [];

            if (Array.isArray(dCosting.expandData) && dCosting.expandData.length > 0) {
              for (const expandData of dCosting.expandData) {
                const expandPrice = Number(expandData?.pricePerUnit || 0);
                const expandQty = Number(expandData?.quantityPerSizCol || 0);
                const customSectionColourSize = {
                  colourWayId: isEmpty(expandData?.colourway?._id) ? 'null' : expandData?.colourway?._id?.toString(),
                  size: !expandData.size?.trim() ? 'null' : expandData.size,
                  lengthId: isEmpty(expandData.length?._id) ? 'null' : expandData.length?._id?.toString(),
                };
                const matchCombination = `${customSectionColourSize.colourWayId}_${customSectionColourSize.size}_${customSectionColourSize.lengthId}`;
                let colorWiseTesting = 0;
                let colorWiseAdditional = 0;
                let colorWiseAdditionalComponent = 0;
                if (expandData?.colourway?._id) {
                  colorWiseTesting = colourwayShare(sumPricePerUnitTestingSectionData, expandData.colourway._id);
                  colorWiseAdditional = colourwayShare(sumPricePerUnitAdditionalSectionData, expandData.colourway._id);
                  colorWiseAdditionalComponent = colourwayShare(sumPricePerUnitAdditionalComponentSectionData, expandData.colourway._id);
                } else {
                  colorWiseTesting = Number(sumPricePerUnitTestingSectionData || 0);
                  colorWiseAdditional = Number(sumPricePerUnitAdditionalSectionData || 0);
                  colorWiseAdditionalComponent = Number(sumPricePerUnitAdditionalComponentSectionData || 0);
                }
                const bomPricePerUnit = Number(cloneBomArr[matchCombination]) || 0;
                const pricePerUnit = colorWiseTesting + colorWiseAdditional + colorWiseAdditionalComponent + expandPrice + bomPricePerUnit;
                const totalPrice = pricePerUnit * expandQty;

                expandData.pricePerUnit = pricePerUnit;
                expandData.totalPrice = totalPrice;
                expandData.multiQuantityRows = [{ quantityPerSizCol: expandQty, pricePerUnit, totalPrice }];
                expandData.defaultRowIndex = 0;
                totalOverAllSumTotalPrice = Number(totalOverAllSumTotalPrice) + Number(totalPrice);

                totalQuantity = Number(totalQuantity) + Number(expandQty);
                totWeightagePricePerUnit = Number(totWeightagePricePerUnit) + Number(pricePerUnit);
                sumOfTotalPrice = Number(sumOfTotalPrice) + Number(totalPrice);
                sumTotalPrice = Number(sumTotalPrice) + Number(totalPrice);
                grandTotalExpandData.push(expandData);
              }
              dCosting.expandData = grandTotalExpandData;
            }
            dCosting.sumOfTotalPrice = sumOfTotalPrice;
            dCosting.sumTotalPrice = sumTotalPrice;

            dCosting.totWeightagePricePerUnit = totWeightagePricePerUnit;
            dCosting.totalQuantity = totalQuantity;
            grandTotalDetailedCosting.push(dCosting);
          }
          setGrandTotalState((prevState) => ({
            ...prevState,
            grandTotal: grandTotalDetailedCosting,
            totalOverAllSumTotalPrice,
          }));
        } else {
          setGrandTotalState((prevState) => ({
            ...prevState,
            grandTotal: [],
            totalOverAllSumTotalPrice: '0',
          }));
        }
      }
    };
    callFn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailedCostingActive, bomTypeArr, quantitiesInfo, testingInfo, additionalInfo, additionalComponentInfo]);

  const expandGrandTotalChild = (index: number | null, expandAll?: boolean): void => {
    setGrandTotalState((state) => {
      const copyState: GrandTotalState = cloneDeep(state);
      if (isBoolean(expandAll)) {
        copyState.grandTotal = (copyState?.grandTotal as GrandTotalRow[])?.map((val) => ({
          ...val,
          isGTExpanded: expandAll as boolean,
          isExpanded: expandAll as boolean,
        }));
      } else if ((index ?? -1) >= 0) {
        const path = `grandTotal[${index}].isGTExpanded`;
        const pathEx = `grandTotal[${index}].isExpanded`;
        const expanded = get(copyState, path, false);
        set(copyState, path, !expanded);
        set(copyState, pathEx, !expanded);
      }
      return { ...state, ...copyState };
    });
    if (isBoolean(expandAll)) {
      setExpandAllState((prev) => ({ ...prev, grandTotalAllExpand: expandAll as boolean }));
    } else if (grandTotalAllExpand) {
      setExpandAllState((prev) => ({ ...prev, grandTotalAllExpand: false }));
    }
  };

  const shouldShowExpand = hideColourCol || hideLengthCol || hideSizeCol;

  const summarySizeDisplay = (data: GrandTotalRow): React.ReactNode => {
    if (colourWayToggle) {
      let displaySize: unknown[] = [];
      if (Array.isArray(totSize) && totSize.length > 0) displaySize = totSize;
      else if (Array.isArray(quantities?.totSize) && quantities.totSize.length > 0) displaySize = quantities.totSize;
      return displaySize.length > 0 ? shortenStringWithTooltip(displaySize.join(', '), 25) : '--';
    }
    return (data?.sizeRange && data?.sizeRange?.[0]?.name) || '';
  };
  const summaryLengthDisplay = (): React.ReactNode => {
    let displayLength: unknown[] = [];
    if (Array.isArray(totLength) && totLength.length > 0) displayLength = totLength;
    else if (Array.isArray(quantities?.totLength) && quantities.totLength.length > 0) displayLength = quantities.totLength;
    return displayLength.length > 0 ? shortenStringWithTooltip(displayLength.join(', '), 25) : '--';
  };
  const childSizeDisplay = (child?: GrandTotalExpandRow): string => {
    if (!colourWayToggle && !sizeLengthToggle) return costingSizes?.join(', ');
    if (hideSizeCol) return child?.size || '';
    return '';
  };

  const rowData = useMemo<GrandTotalGridRow[]>(() => {
    const rows: GrandTotalGridRow[] = [];
    const list: GrandTotalRow[] = Array.isArray(grandTotal) ? (grandTotal as GrandTotalRow[]) : [];

    list.forEach((data, index) => {
      const expandData = data?.expandData || [];
      const isCollapsed = !data?.isGTExpanded && expandData.length !== 1 && !isBreakdown;

      if (isCollapsed) {
        rows.push({
          __cagId: `gt${index}-sum`,
          __rowType: 'summary',
          parentIndex: index,
          __showChevron: expandData.length > 0,
          isExpanded: false,
          colourway: data?.colourway?.name,
          size: summarySizeDisplay(data),
          length: summaryLengthDisplay(),
          quantity: roundOff(data?.sumOfQuantityPerSizCol, 2, true),
          price: roundOff(data?.totWeightagePricePerUnit, 2, true),
          total: `${checkCurrency(currency, data?.sumTotalPrice)} ${roundingOff(data?.sumTotalPrice, 2)}`,
        });
        return;
      }

      if (!(data?.isExpanded || expandData.length === 1 || isBreakdown)) return;

      const expandArr = isBreakdown ? [expandData[0]] : expandData;
      (expandArr || []).forEach((child, chindex) => {
        const allRows = child?.multiQuantityRows?.length ? child.multiQuantityRows : [{ quantityPerSizCol: '', pricePerUnit: '', totalPrice: '' }];
        const defaultRow = allRows[child?.defaultRowIndex ?? 0] ?? allRows[0];

        rows.push({
          __cagId: `gt${index}-c${chindex}`,
          __rowType: 'data',
          parentIndex: index,
          __showChevron: chindex === 0 && !isBreakdown && expandData.length !== 1,
          isExpanded: data?.isGTExpanded,
          colourway: data?.colourway?.name,
          size: childSizeDisplay(child),
          length: child?.length?.name || '',
          quantity: roundOff(defaultRow?.quantityPerSizCol, 2, true),
          price: `${checkCurrency(currency, child?.totalPrice)} ${roundOff(child?.pricePerUnit, 2, true)}`,
          total: `${checkCurrency(currency, child?.totalPrice)} ${roundingOff(child?.totalPrice, 2)}`,
        });
      });
    });

    if (list.length > 1) {
      rows.push({
        __cagId: 'gt-overall-total',
        __rowType: 'grandTotal',
        total: `${checkCurrency(currency, totalOverAllSumTotalPrice)} ${roundingOff(totalOverAllSumTotalPrice, 2)}`,
      });
    }
    return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    grandTotal,
    totalOverAllSumTotalPrice,
    isBreakdown,
    colourWayToggle,
    sizeLengthToggle,
    hideColourCol,
    hideSizeCol,
    hideLengthCol,
    totSize,
    totLength,
    quantities,
    costingSizes,
    currency,
  ]);

  const columnDefs = useMemo<BombiixColumnDefOrGroup<GrandTotalGridRow>[]>(() => {
    const cols: BombiixColumnDefOrGroup<GrandTotalGridRow>[] = [];

    if (shouldShowExpand) {
      cols.push({
        colId: '__expand',
        headerName: '',
        width: 40,
        minWidth: 40,
        maxWidth: 40,
        sortable: false,
        resizable: false,
        flex: 1,
        suppressHeaderMenuButton: true,
        cellRenderer: HierExpandCell,
        cellProps: {
          isParent: (row: GrandTotalGridRow) => row.__showChevron,
          getExpanded: (row: GrandTotalGridRow) => row.isExpanded,
          onToggle: (row: GrandTotalGridRow) => expandGrandTotalChild(row.parentIndex ?? null),
        },
        headerComponent: ExpandAllHeader,
        headerComponentParams: { allExpanded: grandTotalAllExpand, onToggleAll: (val: boolean) => expandGrandTotalChild(null, val) },
      });
    }

    if (hideColourCol) {
      cols.push({
        field: 'colourway',
        headerName: t('DEVLOPMENT_ORDER.EXPAND_VIEW.GRAND_TOTAL.COLOURWAY'),
        cellRenderer: ReadonlyNodeCell,
        width: 150,
        flex: 1,
        sortable: false,
        cellProps: { accessor: (row: GrandTotalGridRow) => row.colourway },
      });
    }
    if (hideSizeCol) {
      cols.push({
        field: 'size',
        flex: 1,
        headerName: t('DEVLOPMENT_ORDER.EXPAND_VIEW.GRAND_TOTAL.SIZE'),
        cellRenderer: ReadonlyNodeCell,
        width: 120,
        sortable: false,
        cellProps: { accessor: (row: GrandTotalGridRow) => row.size },
      });
    }
    if (hideLengthCol) {
      cols.push({
        field: 'length',
        flex: 1,
        headerName: lengthLabel,
        cellRenderer: ReadonlyNodeCell,
        width: 120,
        sortable: false,
        cellProps: { accessor: (row: GrandTotalGridRow) => row.length },
      });
    }

    cols.push({
      field: 'quantity',
      headerName: t('DEVLOPMENT_ORDER.EXPAND_VIEW.GRAND_TOTAL.QUANTITY'),
      cellRenderer: ReadonlyNodeCell,
      width: 120,
      flex: 1,
      sortable: false,
      cellProps: { accessor: (row: GrandTotalGridRow) => row.quantity },
    });
    cols.push({
      field: 'price',
      headerName: t('DEVLOPMENT_ORDER.EXPAND_VIEW.ADDITIONAL_TABLE.PRICE_PER_UNIT'),
      cellRenderer: ReadonlyNodeCell,
      width: 129,
      sortable: false,
      flex: 1,
      cellProps: { accessor: (row: GrandTotalGridRow) => row.price },
    });
    cols.push({
      field: 'total',
      headerName: t('DEVLOPMENT_ORDER.EXPAND_VIEW.GRAND_TOTAL.TOTAL_PRICE'),
      cellRenderer: ReadonlyNodeCell,
      minWidth: 150,
      flex: 1,
      sortable: false,
      cellProps: {
        accessor: (row: GrandTotalGridRow) => row.total,
      },
    });

    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShowExpand, hideColourCol, hideSizeCol, hideLengthCol, grandTotalAllExpand, t, lengthLabel]);

  return (
    <div className="costing-card w-50 p-12px mb-24">
      <p className="product-cp-title">{t('DEVLOPMENT_ORDER.COSTING_DETAILS.GRAND_TOTAL')}</p>

      <div className="table-responsive table-max-h-50">
        <BombiixAGGrid<GrandTotalGridRow>
          revealHeaderControlsOnHover
          sortable={false}
          showColumnMenu={false}
          columnDefs={columnDefs}
          rowData={rowData}
          getRowId={(row) => row.__cagId}
          isRowReadOnly={() => true}
          getRowClass={(row) => {
            if (row?.__rowType === 'grandTotal') return 'cag-hier-row--total';
            if (row?.__rowType === 'summary') return 'cag-hier-row--summary';
            return '';
          }}
          currencySymbol={checkCurrency(currency, undefined, true)}
          gridOptions={{ rowHeight: 52, suppressMovableColumns: true }}
          enableCellCopy
        />
      </div>
    </div>
  );
}

export default GrandTotal;

/**
 * Reads one colourway's share of a section sum. The sum is a plain number when the costing does not
 * vary by colourway; a plain number has no per-colourway share, so it contributes 0.
 * @param sectionSum - result of `sumPricePerUnitTestingAdditionalSection`
 * @param colourwayId - colourway whose share is read
 * @returns the colourway's sum, or 0 when there is none
 */
function colourwayShare(sectionSum: number | Record<string, number>, colourwayId: string): number {
  return typeof sectionSum === 'number' ? 0 : Number(sectionSum[colourwayId] || 0);
}
